
import { supabase } from "@/integrations/supabase/client";
import { AppSettings } from "../types/patientTypes";
import { getCurrentUserId } from "../authUtils";
import { withRetry } from "../retry";
import { logger } from "@/utils/logger";

// Create a settings cache to prevent redundant calls
const settingsCache = {
  data: null as AppSettings | null,
  timestamp: 0
};

// Default settings if none exist
const DEFAULT_SETTINGS: AppSettings = {
  user_id: "",
  theme_preference: 'light',
  enable_image_uploads: false,
  enable_file_uploads: false,
  fast_patient_loading: false,
  patient_view_mode: 'grid',
  // Add camelCase versions for frontend use
  themePreference: 'light',
  enableImageUploads: false,
  enableFileUploads: false,
  fastPatientLoading: false,
  patientViewMode: 'grid'
};

/**
 * Get app settings for the current user with caching
 * @param forceRefresh - If true, bypasses the cache and fetches directly from the database.
 */
export async function getAppSettings(forceRefresh: boolean = false): Promise<AppSettings> {
  try {
    // Check if we have cached settings that are less than 30 seconds old, and not forcing a refresh
    const now = Date.now();
    if (settingsCache.data && now - settingsCache.timestamp < 30000 && !forceRefresh) {
      logger.info("Using cached app settings");
      return settingsCache.data;
    }

    const userId = await getCurrentUserId();

    const { data, error } = await supabase
      .from('app_settings')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      logger.error("Error fetching app settings:", error);
      return DEFAULT_SETTINGS;
    }

    // If settings don't exist yet, create default settings
    if (!data) {
      try {
        const { data: newSettings, error: createError } = await supabase
          .from('app_settings')
          .insert({
            user_id: userId,
            theme_preference: DEFAULT_SETTINGS.theme_preference,
            enable_image_uploads: DEFAULT_SETTINGS.enable_image_uploads,
            enable_file_uploads: DEFAULT_SETTINGS.enable_file_uploads,
            fast_patient_loading: DEFAULT_SETTINGS.fast_patient_loading,
            patient_view_mode: DEFAULT_SETTINGS.patient_view_mode
          })
          .select()
          .single();

        if (createError) {
          if (createError.code === '23505') {
            // Handle race condition: another request created the settings
            logger.info("Settings created by another request, fetching...");
            return await withRetry(() => getAppSettings(), 2, 300, 'getAppSettings after race condition');
          } else {
            logger.error("Error creating app settings:", createError);
            return DEFAULT_SETTINGS;
          }
        }

        // Cache and return the new settings
        const settings = formatSettings(newSettings);
        settingsCache.data = settings;
        settingsCache.timestamp = now;
        return settings;
      } catch (createErr) {
        logger.error("Error in creating default settings:", createErr);
        return DEFAULT_SETTINGS;
      }
    }

    // Format, cache and return existing settings
    const settings = formatSettings(data);
    settingsCache.data = settings;
    settingsCache.timestamp = now;
    return settings;
  } catch (error) {
    logger.error("Error in getAppSettings:", error);
    // Return safe defaults if there's an error
    return DEFAULT_SETTINGS;
  }
}

/**
 * Format database settings object to app settings type
 */
function formatSettings(data: any): AppSettings {
  // Create object with both snake_case (DB) and camelCase (frontend) properties
  return {
    user_id: data.user_id || "",
    theme_preference: data.theme_preference || DEFAULT_SETTINGS.theme_preference,
    enable_image_uploads: data.enable_image_uploads || false,
    enable_file_uploads: data.enable_file_uploads || false,
    fast_patient_loading: data.fast_patient_loading || false,
    // Add camelCase versions for frontend usage
    themePreference: data.theme_preference || DEFAULT_SETTINGS.theme_preference,
    enableImageUploads: data.enable_image_uploads || false,
    enableFileUploads: data.enable_file_uploads || false,
    fastPatientLoading: data.fast_patient_loading || false,
    patientViewMode: data.patient_view_mode || DEFAULT_SETTINGS.patient_view_mode
  };
}

/**
 * Update app settings for the current user with proper error handling for conflicts
 */
export async function updateAppSettings(
  settings: Partial<AppSettings>
): Promise<AppSettings> {
  try {
    const userId = await getCurrentUserId();

    // Ensure user has settings first
    const currentSettings = await getAppSettings();

    // Build update object with only specified fields
    // Convert from camelCase to snake_case for DB storage
    const updateData: any = {};

    if (settings.theme_preference !== undefined) updateData.theme_preference = settings.theme_preference;
    if (settings.themePreference !== undefined) updateData.theme_preference = settings.themePreference;

    if (settings.enable_image_uploads !== undefined) updateData.enable_image_uploads = settings.enable_image_uploads;
    if (settings.enableImageUploads !== undefined) updateData.enable_image_uploads = settings.enableImageUploads;

    if (settings.enable_file_uploads !== undefined) updateData.enable_file_uploads = settings.enable_file_uploads;
    if (settings.enableFileUploads !== undefined) updateData.enable_file_uploads = settings.enableFileUploads;

    if (settings.fast_patient_loading !== undefined) updateData.fast_patient_loading = settings.fast_patient_loading;
    if (settings.fastPatientLoading !== undefined) updateData.fast_patient_loading = settings.fastPatientLoading;

    if (settings.patient_view_mode !== undefined) updateData.patient_view_mode = settings.patient_view_mode;
    if (settings.patientViewMode !== undefined) updateData.patient_view_mode = settings.patientViewMode;

    // If nothing to update, return current settings
    if (Object.keys(updateData).length === 0) {
      return currentSettings;
    }

    // Update settings with retry logic to handle potential conflicts
    const { data, error } = await withRetry(async () => {
      return await supabase
        .from('app_settings')
        .update(updateData)
        .eq('user_id', userId)
        .select()
        .single();
    }, 3, 300, 'updateAppSettings');

    if (error) {
      logger.error("Error updating app settings:", error);
      throw error;
    }

    // Update cache and return the updated settings
    const updatedSettings = formatSettings(data);
    settingsCache.data = updatedSettings;
    settingsCache.timestamp = Date.now();
    return updatedSettings;
  } catch (error) {
    logger.error("Error in updateAppSettings:", error);
    throw error;
  }
}
